import { useEffect, useRef, useState } from 'react';
import { Button, IconButton, Modal, TextField } from '@nike/eds';
import classnames from 'classnames';
import dayjs from 'dayjs';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import nicknameClientAPI, {
  NicknameGroup,
  NicknameOperationRequest,
  ProductItem,
} from '../../clientAPI/nickname';
import { CHANNEL_NAMES, QUERY_KEYS } from '../../constants';
import NBYIcon from 'components/NBYIcon';

export default function NicknameProductsEditModal({
  group,
  onClose,
  isCreating = false,
  readOnly = false,
}: {
  group: NicknameGroup | null;
  onClose: () => void;
  isCreating?: boolean;
  readOnly?: boolean;
}) {
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [nicknameInput, setNicknameInput] = useState('');
  const [productCodeInput, setProductCodeInput] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const [expandedActivations, setExpandedActivations] = useState<
    Record<string, boolean>
  >({});

  const [updateMsg, setUpdateMsg] = useState('');
  const [updateHasError, setUpdateHasError] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();

  const savemutation = useMutation({
    mutationFn: (payload: NicknameOperationRequest) =>
      nicknameClientAPI.nicknameOperation(payload),
    onSuccess: (res) => {
      if (res.errors && res.errors.length > 0) {
        const failedCodes = res.errors
          .map((e) => `${e.productCode} (${e.message})`)
          .join(', ');
        setUpdateHasError(true);
        setUpdateMsg(`Failure: ${failedCodes}`);
      } else {
        setUpdateHasError(false);
        setUpdateMsg('');
        queryClient.invalidateQueries([QUERY_KEYS.NICKNAME_DICTIONARY]);
        queryClient.invalidateQueries([QUERY_KEYS.NICKNAME_ALL_PRODUCTS]);
        handleClose();
      }
    },
    onError: () => {
      setUpdateHasError(true);
      setUpdateMsg('Save failed. Please try again.');
    },
  });

  useEffect(() => {
    setProducts(group?.products ? [...group.products] : []);
    setNicknameInput(group?.nickname ?? '');
    setProductCodeInput('');
    setAddError('');
  }, [group]);

  const handleAddProduct = async () => {
    const code = productCodeInput.trim();
    if (!code) return;
    if (products.some((p) => p.productCode === code)) {
      setAddError('Product already in list.');
      return;
    }
    setIsAdding(true);
    setAddError('');
    try {
      const results = await nicknameClientAPI.findProducts({
        productCode: code,
      });
      if (results.length === 0) {
        setProducts((prev) => [
          ...prev,
          {
            _id: code,
            productCode: code,
            styleCode: '',
            globalProductId: '',
            copy: { title: '', subtitle: '' },
            squarishUrl: '',
            source: {},
          } as ProductItem,
        ]);
      } else {
        const p = results[0];
        setProducts((prev) => [...prev, p]);
      }
      setProductCodeInput('');
      inputRef.current?.focus();
    } catch {
      setAddError('Failed to fetch product. Please try again.');
    } finally {
      setIsAdding(false);
    }
  };

  const getPendingUpdatePayload = () => {
    if (nicknameInput.trim() === '') {
      return null;
    }
    const originalProducts = group?.products ?? [];
    const toAdd = products
      .filter((p) => !originalProducts.some((op) => op._id === p._id))
      .map((p) => p.productCode || p._id);
    const toRemove = originalProducts
      .filter((op) => !products.some((p) => p._id === op._id))
      .map((p) => p.productCode || p._id);
    if (toAdd.length === 0 && toRemove.length === 0) {
      return null;
    }
    return {
      nickname: nicknameInput.trim(),
      productsToAdd: toAdd,
      productsToRemove: toRemove,
    };
  };
  const handleClose = () => {
    onClose();
    setNicknameInput('');
    setProductCodeInput('');
    setAddError('');
    setProducts([]);
    setUpdateMsg('');
    setUpdateHasError(false);
    savemutation.reset();
  };

  const handleSave = () => {
    const payload = getPendingUpdatePayload();
    if (!payload) return;
    savemutation.mutate(payload);
  };

  return (
    <Modal
      isOpen={isCreating || group !== null}
      onDismiss={handleClose}
      headerSlot={<div>{isCreating ? 'Create' : 'Edit'}</div>}
      footerSlot={
        <div className='tw-flex tw-gap-2'>
          {getPendingUpdatePayload() && (
            <Button
              size='small'
              variant='primary'
              onClick={handleSave}
              disabled={savemutation.isLoading}
            >
              {savemutation.isLoading ? 'Saving…' : 'Save'}
            </Button>
          )}
          <Button
            onClick={handleClose}
            size='small'
            variant='secondary'
            disabled={savemutation.isLoading}
          >
            Close
          </Button>
        </div>
      }
    >
      <div className='tw-flex tw-flex-col tw-max-h-[60vh] tw-min-h-[300px] tw-w-[60vw]'>
        <div className='tw-mb-4 tw-text-left'>
          <TextField
            id='nickname-detail-nickname'
            label='Nickname'
            value={nicknameInput}
            readOnly={!isCreating}
            onChange={(e) =>
              setNicknameInput((e.target as HTMLInputElement).value)
            }
            subtitle=''
          />
        </div>
        <div className='tw-flex-1 tw-overflow-y-auto tw-pr-1'>
          {products.map((product: ProductItem) => (
            <div
              key={product._id}
              className='tw-group tw-relative tw-flex tw-gap-4 tw-flex-wrap tw-items-start tw-pb-4 last:tw-border-0 last:tw-pb-0 tw-rounded tw-border tw-border-transparent hover:tw-border-slate-200 hover:tw-shadow-md tw-p-2 tw-transition-all'
            >
              <span className='tw-absolute tw-top-0 tw-right-0 tw-invisible group-hover:tw-visible'>
                <IconButton
                  icon='Delete'
                  size='small'
                  variant='ghost'
                  label={`Remove ${product.copy?.title || product._id}`}
                  disabled={readOnly}
                  onClick={() => {
                    setProducts((prev) =>
                      prev.filter((p) => p._id !== product._id),
                    );
                  }}
                />
              </span>
              {product.squarishUrl ? (
                <img
                  src={product.squarishUrl}
                  alt={product.copy?.title || product._id}
                  className='tw-w-20 tw-h-20 tw-object-cover tw-rounded tw-flex-shrink-0 tw-bg-slate-100'
                />
              ) : (
                <div className='tw-w-20 tw-h-20 tw-rounded tw-bg-slate-100 tw-flex-shrink-0 tw-flex tw-items-center tw-justify-center'>
                  <span className='tw-text-slate-400 tw-text-xs'>No image</span>
                </div>
              )}
              <div className='tw-flex tw-flex-col tw-gap-0.5 tw-text-left'>
                <span className='eds-type--body-2 tw-font-semibold tw-text-slate-800'>
                  {product.copy?.title || product._id}
                </span>
                {product.copy?.subtitle && (
                  <span className='eds-type--body-3 tw-text-slate-500'>
                    {product.copy.subtitle}
                  </span>
                )}
                <div className='tw-flex tw-flex-wrap tw-gap-2'>
                  <span className='tw-text-xs tw-text-slate-400 tw-font-bold'>
                    {product.productCode}
                  </span>
                  {product.productSubType === 'CUSTOMIZABLE' && (
                    <NBYIcon
                      className='tw-inline-block tw-align-middle'
                      width={16}
                      height={16}
                    />
                  )}
                  {product.productSubType === 'CUSTOMIZED' && (
                    <NBYIcon
                      className='tw-inline-block tw-align-middle'
                      width={16}
                      height={16}
                    />
                  )}
                  {product.productSubType !== 'STANDARD' && (
                    <span className='tw-text-xs tw-text-slate-400 tw-font-bold'>
                      {product.productSubType}
                    </span>
                  )}
                </div>
              </div>
              {product.activations?.length > 0 && (
                <div className='tw-ml-auto tw-flex tw-flex-col tw-gap-1 tw-shrink-0 tw-min-w-[50%]'>
                  <button
                    className='tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-gray-500 tw-cursor-pointer tw-bg-transparent tw-border-none tw-p-0 hover:tw-text-gray-800'
                    onClick={() =>
                      setExpandedActivations((prev) => ({
                        ...prev,
                        [product._id]: !prev[product._id],
                      }))
                    }
                  >
                    <span
                      className={classnames(
                        'tw-transition-transform tw-duration-200',
                        {
                          'tw-rotate-90': expandedActivations[product._id],
                        },
                      )}
                    >
                      ▶
                    </span>
                    <span>
                      Channels (
                      {
                        new Set(product.activations.map((a) => a.channelId))
                          .size
                      }
                      )
                    </span>
                  </button>
                  {expandedActivations[product._id] && (
                    <div className='tw-mt-1 tw-flex tw-flex-col tw-gap-1'>
                      {Object.values(
                        product.activations.reduce<
                          Record<
                            string,
                            {
                              channelId: string;
                              ranges: {
                                start?: string;
                                end?: string;
                                status: string;
                                statusModifier?: string;
                              }[];
                            }
                          >
                        >((acc, a) => {
                          const key = a.channelId;
                          if (!acc[key]) {
                            acc[key] = { channelId: a.channelId, ranges: [] };
                          }
                          acc[key].ranges.push({
                            start: a.start,
                            end: a.end,
                            status: a.status,
                            statusModifier: a.statusModifier,
                          });
                          return acc;
                        }, {}),
                      ).map((a) => {
                        const ckey = `${product.productCode}_${a.channelId}`;
                        return (
                          <div
                            key={ckey}
                            className='tw-flex tw-items-start tw-gap-2 tw-text-xs'
                          >
                            <span className='tw-font-bold tw-w-20 tw-shrink-0 tw-rounded tw-px-2 tw-py-0.5 tw-border tw-border-solid tw-border-sky-900 tw-text-gray-600'>
                              {CHANNEL_NAMES[a.channelId] ?? a.channelId}
                            </span>
                            <div className='tw-flex tw-flex-col tw-gap-0.5'>
                              {a.ranges.map((r, i) => {
                                const status =
                                  r.status === 'ACTIVE'
                                    ? 'ACTIVE'
                                    : r.statusModifier || r.status;
                                return (
                                  <div
                                    key={i}
                                    className='tw-flex tw-items-center tw-gap-2'
                                  >
                                    <span
                                      className={classnames(
                                        'tw-inline-block tw-font-bold tw-rounded tw-px-2 tw-py-0.5 tw-min-w-24 tw-shrink-0 tw-text-center',
                                        {
                                          'tw-text-emerald-200 tw-bg-emerald-500':
                                            status === 'ACTIVE',
                                          'tw-text-orange-200 tw-bg-orange-500':
                                            status === 'INACTIVE',
                                          'tw-text-yellow-200 tw-bg-yellow-500':
                                            status === 'HOLD',
                                          'tw-text-gray-200 tw-bg-gray-500': ![
                                            'ACTIVE',
                                            'INACTIVE',
                                            'HOLD',
                                          ].includes(status),
                                        },
                                      )}
                                    >
                                      {status}
                                    </span>
                                    <span className='tw-text-gray-500'>
                                      {r.start
                                        ? dayjs(r.start).format(
                                            'YYYY-MM-DD HH:mm',
                                          )
                                        : '—'}
                                    </span>
                                    <span className='tw-text-gray-400'>→</span>
                                    <span className='tw-text-gray-500'>
                                      {r.end
                                        ? dayjs(r.end).format(
                                            'YYYY-MM-DD HH:mm',
                                          )
                                        : '—'}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {!readOnly && (
          <div className='tw-border-t tw-border-slate-200 tw-pt-3 tw-mt-3'>
            <div className='tw-flex tw-items-center tw-gap-2'>
              <input
                ref={inputRef}
                type='text'
                value={productCodeInput}
                onChange={(e) => {
                  setProductCodeInput(e.target.value);
                  setAddError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleAddProduct()}
                placeholder='Enter product code…'
                disabled={isAdding}
                className='tw-flex-1 tw-border tw-border-slate-300 tw-rounded tw-px-3 tw-py-1.5 tw-text-sm tw-outline-none focus:tw-border-indigo-500 disabled:tw-opacity-50'
              />
              <IconButton
                icon='Plus'
                size='small'
                variant='ghost'
                label='Add product'
                disabled={isAdding || !productCodeInput.trim()}
                onClick={handleAddProduct}
              />
            </div>
            {addError && (
              <p className='tw-text-red-500 tw-text-xs tw-mt-1'>{addError}</p>
            )}
          </div>
        )}
        {updateHasError && updateMsg && (
          <p className='tw-text-red-500 tw-text-sm tw-mt-3'>{updateMsg}</p>
        )}
      </div>
    </Modal>
  );
}
